import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { LOST_REASON_LABELS, money } from './constants';
import './Screens.css';

const pct = (part, whole) => (Number(whole) > 0 ? `${Math.round((Number(part) / Number(whole)) * 100)}%` : '—');

const Reports = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const fetchReports = useCallback(async () => {
    try {
      const response = await api.get('/crm/reports');
      setData(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to build reports');
    }
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  if (error) return <div className="crm-screen"><p className="banner error">{error}</p></div>;
  if (!data) return <div className="crm-screen"><p className="loading">Loading…</p></div>;

  const s = data.summary || {};
  const wl = data.winLoss || {};
  const decided = (wl.won || 0) + (wl.lost || 0);

  const lossReasons = (wl.losses || []).reduce((acc, l) => {
    const key = l.reason || 'other';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const hasData = Number(s.committed_value) > 0 || (data.bySport || []).length > 0;

  return (
    <div className="crm-screen">
      <div className="screen-header">
        <div>
          <h1>Reports</h1>
          <p className="subtitle">
            Forecasting gets useful once a full cycle of history is in.
          </p>
        </div>
        <span className="season-context">Now selling the {data.year} season</span>
      </div>

      <div className="tiles">
        <div className="tile good">
          <div className="tile-value">{money(s.committed_value)}</div>
          <div className="tile-label">Committed · {s.won_count || 0} ordered</div>
        </div>
        <div className="tile">
          <div className="tile-value">{money(s.expected_value)}</div>
          <div className="tile-label">Still open · {s.open_count || 0}</div>
        </div>
        <div className="tile warn">
          <div className="tile-value">{money(s.committed_margin)}</div>
          <div className="tile-label">
            Gross margin · {pct(s.committed_margin, s.committed_value)}
          </div>
        </div>
        <div className="tile bad">
          <div className="tile-value">{money(s.lost_value)}</div>
          <div className="tile-label">Lost · {s.lost_count || 0}</div>
        </div>
        <div className="tile">
          <div className="tile-value">{decided ? pct(wl.won, decided) : '—'}</div>
          <div className="tile-label">Win rate</div>
        </div>
      </div>

      {!hasData ? (
        <div className="card">
          <p className="empty">
            <strong>Nothing to report on yet</strong>
            Numbers appear here as seasons get marked ordered with their values filled in.
          </p>
        </div>
      ) : (
        <>
          <div className="card">
            <h2>By sport</h2>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Sport</th><th className="num">Seasons</th><th className="num">Revenue</th>
                    <th className="num">Cost</th><th className="num">Margin</th><th className="num">Margin %</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.bySport || []).map(row => (
                    <tr key={row.sport}>
                      <td>{row.sport}</td>
                      <td className="num">{row.seasons}</td>
                      <td className="num">{money(row.revenue)}</td>
                      <td className="num muted">{money(row.cost)}</td>
                      <td className="num pos">{money(row.margin)}</td>
                      <td className="num">{pct(row.margin, row.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <h2>By account</h2>
            <p className="muted" style={{ fontSize: '0.8125rem', margin: '-8px 0 12px' }}>
              Biggest is not the same list as most profitable — watch both columns.
            </p>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Account</th><th>Class</th><th className="num">Seasons</th>
                    <th className="num">Revenue</th><th className="num">Margin</th><th className="num">Margin %</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.byAccount || []).map(row => (
                    <tr
                      key={row.organization_id}
                      className="clickable"
                      onClick={() => navigate(`/crm/organizations/${row.organization_id}`)}
                    >
                      <td>{row.organization_name}</td>
                      <td className="muted">{row.uil_classification || '—'}</td>
                      <td className="num">{row.seasons}</td>
                      <td className="num">{money(row.revenue)}</td>
                      <td className="num pos">{money(row.margin)}</td>
                      <td className="num">{pct(row.margin, row.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {Object.keys(lossReasons).length > 0 && (
            <div className="card">
              <h2>Why we lost</h2>
              <div className="table-scroll">
                <table style={{ minWidth: 320 }}>
                  <thead><tr><th>Reason</th><th className="num">Count</th></tr></thead>
                  <tbody>
                    {Object.entries(lossReasons)
                      .sort((a, b) => b[1] - a[1])
                      .map(([reason, count]) => (
                        <tr key={reason}>
                          <td>{LOST_REASON_LABELS[reason] || reason}</td>
                          <td className="num">{count}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Reports;
